/**
 * File: useScrollAnimation.js
 * Path: /src/hooks/useScrollAnimation.js
 * Description: Hook that applies CSS classes to elements when they scroll into view.
 * Key Features: Animates elements with configurable delay options and animation types.
 * Maintenance Notes: Used by page components to trigger animations on scroll.
 */

import { useEffect } from 'react';

/**
 * Custom hook to apply animations to elements when they scroll into view
 * @param {string} selector - CSS selector for elements to animate
 * @param {string} animationClass - CSS class to add when elements come into view
 * @param {Object} options - IntersectionObserver options
 * @param {number} options.threshold - A number between 0 and 1 indicating the percentage that should be visible
 * @param {string} options.rootMargin - Margin around the root
 */
const useScrollAnimation = (
  selector = '.animate-on-scroll',
  animationClass = 'is-visible',
  options = { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
) => {
  useEffect(() => {
    const { threshold, rootMargin } = options;
    const elements = document.querySelectorAll(selector);
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add(animationClass);
          
          // If the element has a data-delay attribute, add a style for animation-delay
          if (entry.target.dataset.delay) {
            entry.target.style.animationDelay = `${entry.target.dataset.delay}ms`;
            entry.target.style.transitionDelay = `${entry.target.dataset.delay}ms`;
          }
          
          // Check if the animation should only run once
          if (entry.target.dataset.once !== 'false') {
            observer.unobserve(entry.target);
          }
        } else if (entry.target.dataset.once === 'false') {
          // If the element should animate every time, remove the class when it's not visible
          entry.target.classList.remove(animationClass);
        }
      });
    }, { threshold, rootMargin });
    
    elements.forEach(element => {
      observer.observe(element);
    });
    
    return () => {
      elements.forEach(element => {
        observer.unobserve(element);
      });
    };
  }, [selector, animationClass, options]);
};

export default useScrollAnimation;